import { isPatientApp } from '@/config/constants';
import encryptedStorage from '../../stores/encryptedStorage';
import store from '../../stores/store';
import { hasOwnProperty } from '../../utils/utils';
import { DEVICE_TOKEN_KEY } from '@/utils/secureDeviceToken';

export const compareTimeStamps = (patientData, sets) => {
  const { wordsets, sentencesets, textsets } = patientData;
  const allActiveSets = [...wordsets, ...sentencesets, ...textsets];
  const setsThatNeedSyncing = allActiveSets.map((set) => {
    const { _id, timestamp } = set;
    const localSet = sets[_id];
    if (!localSet) return;
    const { activeWordset, exerciseClass } = localSet;
    if (!activeWordset) return _id;
    const innerKey = exerciseClass + 'set';
    const localTimeStamp = activeWordset[innerKey].updatedAt;
    // Set is not synced with the server set
    if (localTimeStamp !== timestamp) return _id;
    return;
  });
  return setsThatNeedSyncing.filter(Boolean);
};

export async function loadSets() {
  const sets = {};
  const setKeys = await encryptedStorage()
    .listItems()
    .then((keys) =>
      keys.filter(
        (k) =>
          k !== '[encryptedStorage][0]' &&
          k !== 'stayLoggedIn' &&
          k !== 'minimumAppVersion' &&
          k !== 'seenUpdates' &&
          k !== 'unprotectedDevice' &&
          k !== 'unsupportedIosVersionContinue' &&
          k !== 'unsupportedAndroidVersionContinue' &&
          !k.includes('sessionBuffer') &&
          !k.includes('starContainer') &&
          k !== DEVICE_TOKEN_KEY,
      ),
    );

  const allPromise = setKeys.map(async (key) => {
    let item = await encryptedStorage().getItem(key);
    return item;
  });
  const res = await Promise.all(allPromise);

  res.forEach((item, index) => {
    const key = setKeys[index];
    sets[key] = item;
  });
  return sets;
}

async function cleanSets(sets, { setsToKeep, patientIdsToKeep }) {
  const returnSets = {};
  const sessionBuffer = store.getState().storedSessions;
  const resultsBuffer = store.getState().resultsWithKey;
  const starsContainer = store.getState().storedStars;
  const removeFromEncryptedStorage = async (id) =>
    await encryptedStorage().removeItem(id);

  for (let key of Object.keys(sets)) {
    const localWordset = sets[key];
    const ifCondition =
      hasOwnProperty(sessionBuffer, key) || hasOwnProperty(starsContainer, key);
    if (ifCondition) {
      if (typeof patientIdsToKeep[key] === 'undefined') {
        await removeFromEncryptedStorage(key);
      }
    } else if (!hasOwnProperty(resultsBuffer, key)) {
      if (typeof setsToKeep[key] === 'undefined') {
        await removeFromEncryptedStorage(key);
      } else {
        returnSets[key] = localWordset;
      }
    }
  }

  return returnSets;
}

export async function loadAndClean(setsToKeep, patientIdsToKeep) {
  const sets = await loadSets();
  return await cleanSets(sets, { patientIdsToKeep, setsToKeep });
}

const createSet = async (setId, patientId, exerciseClass) => {
  let localSet = await encryptedStorage().getItem(setId);

  if (!localSet) {
    localSet = {};
    localSet.preparedSet = {};
    localSet.preparedSet.date = {};
    localSet.patientId = patientId;
    localSet.exerciseSettings = {};
    localSet.sync = isPatientApp ? true : false;
    localSet.exerciseClass = exerciseClass;

    // if is textset then create default text settings
    if (exerciseClass === 'text') {
      localSet.exerciseSettings.audioSpeedSetting = 'normal';
      localSet.exerciseSettings.Verstehen = {};
      localSet.exerciseSettings.Verstehen.setting = 'closed';
      localSet.exerciseSettings.Verstehen.value = 4;
      localSet.exerciseSettings.Verstehen.active = true;
      localSet.exerciseSettings.Sprechen = {};
      localSet.exerciseSettings.Sprechen.setting = 'questions';
      localSet.exerciseSettings.Sprechen.active = true;
      localSet.exerciseSettings.Sprechen.value = 600;
      localSet.exerciseSettings.Lesen = {};
      localSet.exerciseSettings.Lesen.setting = 'closed';
      localSet.exerciseSettings.Lesen.value = 4;
      localSet.exerciseSettings.Lesen.active = true;
      localSet.exerciseSettings.Schreiben = {};
      localSet.exerciseSettings.Schreiben.setting = 'gap';
      localSet.exerciseSettings.Schreiben.active = true;
    }
    // else (if word / sentenceset) create default word/sentence settings
    else {
      localSet.exerciseSettings.Verstehen = {};
      localSet.exerciseSettings.Verstehen.setting = 'related';
      localSet.exerciseSettings.Verstehen.value = 4;
      localSet.exerciseSettings.Verstehen.active = true;
      localSet.exerciseSettings.Sprechen = {};
      localSet.exerciseSettings.Sprechen.setting = 'record';
      localSet.exerciseSettings.Sprechen.value = 5;
      localSet.exerciseSettings.Sprechen.active = true;
      localSet.exerciseSettings.Lesen = {};
      localSet.exerciseSettings.Lesen.setting = 'related';
      localSet.exerciseSettings.Lesen.value = 4;
      localSet.exerciseSettings.Lesen.active = true;
      localSet.exerciseSettings.Schreiben = {};
      localSet.exerciseSettings.Schreiben.setting = 'anagram';
      localSet.exerciseSettings.Schreiben.value = 3;
      localSet.exerciseSettings.Schreiben.active = true;
      localSet.cardSettings = {};
      localSet.cardSettings.setting = 'related';
      localSet.cardSettings.images = 1;
    }
    await encryptedStorage().setItem(setId, localSet);
  }
};

export const generateSets = (patients) => {
  const setsToKeep = {};
  const patientIdsToKeep = {};
  const promises = [];

  patients.forEach((patient) => {
    patientIdsToKeep[patient._id] = true;

    patient.wordsets.forEach((wordset) => {
      setsToKeep[wordset._id] = true;
      promises.push(createSet(wordset._id, patient._id, 'word'));
    });

    patient.sentencesets.forEach((sentenceset) => {
      setsToKeep[sentenceset._id] = true;
      promises.push(createSet(sentenceset._id, patient._id, 'sentence'));
    });

    patient.textsets.forEach((textset) => {
      setsToKeep[textset._id] = true;
      promises.push(createSet(textset._id, patient._id, 'text'));
    });
  });

  return {
    promises,
    patientIdsToKeep,
    setsToKeep,
  };
};
